/**
 * src/lib/voice/suggestBundle.js — Bundle suggestion for transcript actions
 * AC Nautitech — Voice Breadcrumbs
 *
 * CEO 2026-06-18: when converting transcript action items into tasks, the task
 * should land in the right bundle with as few clicks as possible. This picks
 * the best-matching EXISTING bundle for an action so the convert UI can
 * pre-fill it (with an inline dropdown to override when the guess is wrong).
 *
 * Resolution order:
 *   1. Explicit hint — a classifier-supplied or prior user-edited bundle id /
 *      name that resolves to a real bundle in the list (forward-compatible
 *      with a future classifier `bundle_hint`).
 *   2. Heuristic score — product-category exact match (strong) + shared
 *      title/description keywords (weak). Best score above threshold wins.
 *
 * Returns the matched bundle object, or null when nothing is confident enough
 * (the picker then defaults to "No bundle"). Pure + exported for unit testing.
 */

// Short, generic words that carry no signal for bundle matching.
const STOPWORDS = new Set([
  'the', 'and', 'for', 'with', 'from', 'this', 'that', 'into', 'onto', 'task',
  'tasks', 'item', 'items', 'need', 'needs', 'make', 'fix', 'check', 'new',
  'add', 'all', 'have', 'has', 'get', 'will', 'should', 'must', 'job',
]);

function tokenize(str) {
  return new Set(
    String(str || '')
      .toLowerCase()
      .split(/[^a-z0-9]+/)
      .filter(tok => tok.length >= 3 && !STOPWORDS.has(tok)),
  );
}

function norm(str) {
  return String(str || '').trim().toLowerCase();
}

/**
 * @param {Object} action  - a derived_action ({ title, description, detail_json, user_edits })
 * @param {Array}  bundles - candidate bundles already filtered to the action's project
 * @returns {Object|null}  - the suggested bundle, or null
 */
export function suggestBundleForAction(action = {}, bundles = []) {
  if (!Array.isArray(bundles) || bundles.length === 0) return null;
  const dj = action.detail_json || {};

  // 1. Explicit hint wins (user edit > classifier hint).
  const hintId = action.user_edits?.bundle_id || dj.bundle_id || dj.bundle_hint_id;
  if (hintId) {
    const byId = bundles.find(b => b.id === hintId);
    if (byId) return byId;
  }
  const hintName = dj.bundle_hint || dj.bundle_name;
  if (hintName) {
    const byName = bundles.find(b => norm(b.title || b.name) === norm(hintName));
    if (byName) return byName;
  }

  // 2. Heuristic scoring.
  const actionCategory = norm(dj.product_category);
  const hay = tokenize(`${action.title || ''} ${action.description || ''}`);

  let best = null;
  let bestScore = 0;
  for (const b of bundles) {
    let score = 0;
    // Product-category exact match is a strong signal.
    const bundleCategory = norm(b.product_category || b.product_type);
    if (actionCategory && bundleCategory && actionCategory === bundleCategory) score += 3;
    // Shared keywords between action text and bundle title.
    const needle = tokenize(`${b.title || ''} ${b.name || ''}`);
    for (const tok of needle) if (hay.has(tok)) score += 1;
    if (score > bestScore) { bestScore = score; best = b; }
  }

  return bestScore >= 1 ? best : null;
}
